import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3, Bell, CalendarDays, ChevronDown, ChevronRight, CircleDollarSign, Clock3, Command,
  LayoutDashboard, LogOut, Menu, Moon, Package, Plus, Search, Settings, Scissors, Sun, Trash2,
  Users, UserRound, WalletCards, X, Check, Sparkles, MessageSquare
} from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_grooming-desk-6/artifacts/oxrobxi7_WhatsApp%20Image%202026-09-17%20at%2021.33.00.jpeg";

axios.defaults.withCredentials = true;

const money = (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("barbearia_token") || ""}` });

const nav = [
  { label: "Visão geral", icon: LayoutDashboard, path: "/" },
  { label: "Agendamentos", icon: CalendarDays, path: "/appointments" },
  { label: "Clientes", icon: Users, path: "/clients" },
  { label: "Barbeiros", icon: UserRound, path: "/barbers" },
  { label: "Serviços", icon: Scissors, path: "/services" },
  { label: "Planos mensais", icon: WalletCards, path: "/plans" },
  { label: "Produtos", icon: Package, path: "/products" },
  { label: "Caixa", icon: CircleDollarSign, path: "/cash" },
  { label: "Relatórios", icon: BarChart3, path: "/reports" },
  { label: "Notificações", icon: MessageSquare, path: "/notifications" },
];

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<ClientLogin />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/client/*" element={<Protected role="client"><ClientArea /></Protected>} />
      <Route path="*" element={<Protected role="staff"><Shell /></Protected>} />
    </Routes>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(location.hash.replace("#", ""));
    const session_id = params.get("session_id");
    if (!session_id) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const r = await axios.post(`${API}/auth/google/session`, { session_id }, { withCredentials: true });
        localStorage.setItem("barbearia_token", r.data.token);
        localStorage.setItem("barbearia_user", JSON.stringify(r.data.user));
        window.history.replaceState({}, "", "/");
        // Google Auth padrão cria usuário como cliente → área do cliente
        const role = r.data.user?.role;
        navigate(role === "admin" || role === "barber" ? "/" : "/client");
      } catch (e) {
        navigate("/login");
      }
    })();
  }, [location.hash, navigate]);
  return <div className="fullscreen-loader"><div className="loader-ring" /> Autenticando...</div>;
}

function Protected({ children, role }) {
  const token = localStorage.getItem("barbearia_token");
  if (!token) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem("barbearia_user") || "{}");
  const isStaff = user.role === "admin" || user.role === "barber";
  if (role === "staff" && !isStaff) return <Navigate to="/client" replace />;
  if (role === "client" && isStaff) return <Navigate to="/" replace />;
  return children;
}

// ============ ADMIN LOGIN (equipe) ============
function AdminLogin() {
  const navg = useNavigate();
  const [form, setForm] = useState({ email: "admin@gabrielcorte.com", password: "Gabriel@2025" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // se já está logado como staff, entra direto no painel
    const u = JSON.parse(localStorage.getItem("barbearia_user") || "{}");
    if (localStorage.getItem("barbearia_token") && (u.role === "admin" || u.role === "barber")) navg("/");
  }, [navg]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, form);
      if (r.data.user.role !== "admin" && r.data.user.role !== "barber") {
        setError("Este acesso é restrito à equipe. Clientes acessam por /login.");
        setBusy(false); return;
      }
      localStorage.setItem("barbearia_token", r.data.token);
      localStorage.setItem("barbearia_user", JSON.stringify(r.data.user));
      navg("/");
    } catch (e) {
      setError(e.response?.data?.detail || "Não foi possível continuar");
    }
    setBusy(false);
  };

  return (
    <main className="admin-login-page">
      <div className="admin-login-card">
        <img data-testid="admin-login-logo" className="admin-login-logo" src={LOGO} alt="Logo Barbearia Gabriel do Corte" />
        <p className="eyebrow">ÁREA DA EQUIPE</p>
        <h1>Painel administrativo</h1>
        <p className="muted">Acesso restrito a administradores e barbeiros da barbearia.</p>
        <form onSubmit={submit} className="admin-login-form">
          <label>E-mail corporativo<input data-testid="admin-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required autoFocus /></label>
          <label>Senha<input data-testid="admin-password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required /></label>
          {error && <div data-testid="admin-login-error" className="error-msg">{error}</div>}
          <button data-testid="admin-login-submit" className="gold-btn full" type="submit" disabled={busy}>
            {busy ? "Entrando..." : "Entrar no painel"} <span>↗</span>
          </button>
        </form>
        <div className="admin-login-foot">
          <a data-testid="go-to-client-login" href="/login">Você é cliente? Acesse por aqui →</a>
        </div>
      </div>
      <div className="admin-login-bg" />
    </main>
  );
}

// ============ CLIENT LOGIN ============
function ClientLogin() {
  const navg = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", code: "", new_password: "" });
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("barbearia_user") || "{}");
    if (localStorage.getItem("barbearia_token")) {
      if (u.role === "admin" || u.role === "barber") navg("/");
      else navg("/client");
    }
  }, [navg]);

  const switchMode = (m) => { setMode(m); setError(""); setInfo(""); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setInfo("");
    try {
      if (mode === "login" || mode === "register") {
        const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
        const payload = mode === "register" ? { ...form, role: "client" } : form;
        const r = await axios.post(`${API}${endpoint}`, payload);
        if (r.data.user.role === "admin" || r.data.user.role === "barber") {
          setError("Este acesso é somente para clientes. Se você faz parte da equipe, acesse por /admin.");
          setBusy(false); return;
        }
        localStorage.setItem("barbearia_token", r.data.token);
        localStorage.setItem("barbearia_user", JSON.stringify(r.data.user));
        navg("/client");
      } else if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setInfo("Se o cadastro existir, enviamos um código de 6 dígitos para o e-mail cadastrado. Cole aqui em até 15 minutos.");
        setMode("reset");
      } else if (mode === "reset") {
        await axios.post(`${API}/auth/reset-password`, { email: form.email, code: form.code, new_password: form.new_password });
        setInfo("Senha alterada com sucesso. Entre com sua nova senha.");
        setForm({ ...form, password: "", code: "", new_password: "" });
        setMode("login");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Não foi possível continuar");
    }
    setBusy(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const titles = {
    login: { eyebrow: "ÁREA DO CLIENTE", h: "Bem-vindo de volta", sub: "Entre para agendar seu próximo corte." },
    register: { eyebrow: "NOVO CADASTRO", h: "Crie sua conta", sub: "Cadastre-se em segundos e agende agora." },
    forgot: { eyebrow: "RECUPERAR SENHA", h: "Esqueceu a senha?", sub: "Digite seu e-mail cadastrado. Enviaremos um código para você." },
    reset: { eyebrow: "NOVA SENHA", h: "Digite o código recebido", sub: "Confira sua caixa de entrada. O código expira em 15 minutos." },
  };
  const t = titles[mode];

  return (
    <main className="login-page">
      <div className="login-visual">
        <img data-testid="login-logo" className="login-logo" src={LOGO} alt="Logo Barbearia Gabriel do Corte" />
        <p className="eyebrow">BARBEARIA GABRIEL DO CORTE</p>
        <h1>Seu horário,<br /><em>na palma da mão.</em></h1>
        <p className="login-note">Agende cortes, assine planos e acompanhe sua fidelidade — tudo direto no seu perfil.</p>
        <div className="login-rule" />
        <ul className="login-highlights">
          <li><Check size={14} /> Escolha seu barbeiro favorito</li>
          <li><Check size={14} /> Assinaturas mensais com desconto</li>
          <li><Check size={14} /> Pontos de fidelidade a cada visita</li>
        </ul>
      </div>
      <form className="login-form" onSubmit={submit}>
        <div className="mobile-brand"><img data-testid="mobile-login-logo" src={LOGO} alt="Logo" /> <span>GABRIEL DO CORTE</span></div>
        <p className="eyebrow">{t.eyebrow}</p>
        <h2>{t.h}</h2>
        <p className="muted">{t.sub}</p>

        {mode === "register" && (
          <>
            <label>Nome completo<input data-testid="register-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></label>
            <label>Telefone (WhatsApp)<input data-testid="register-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(11) 99999-9999" required /></label>
          </>
        )}

        {(mode === "login" || mode === "register" || mode === "forgot" || mode === "reset") && (
          <label>{mode === "forgot" || mode === "reset" ? "E-mail cadastrado" : "E-mail ou telefone"}
            <input data-testid="login-identity-input" type={(mode === "forgot" || mode === "reset") ? "email" : "text"} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></label>
        )}

        {(mode === "login" || mode === "register") && (
          <label>Senha<input data-testid="login-password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required /></label>
        )}

        {mode === "reset" && (
          <>
            <label>Código recebido<input data-testid="reset-code-input" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="6 dígitos" maxLength={6} required /></label>
            <label>Nova senha<input data-testid="reset-new-password-input" type="password" value={form.new_password} onChange={e => setForm({ ...form, new_password: e.target.value })} minLength={6} required /></label>
          </>
        )}

        {error && <div data-testid="login-error" className="error-msg">{error}</div>}
        {info && <div data-testid="login-info" className="info-msg">{info}</div>}

        <button data-testid="login-submit-button" className="gold-btn full" type="submit" disabled={busy}>
          {busy ? "..." : (mode === "login" ? "Acessar minha conta" : mode === "register" ? "Criar conta" : mode === "forgot" ? "Enviar código" : "Salvar nova senha")} <span>↗</span>
        </button>

        {(mode === "login" || mode === "register") && (
          <button data-testid="google-login-button" type="button" className="ghost-btn full" onClick={google}>Continuar com Google</button>
        )}

        <div className="form-foot">
          {mode === "login" && (
            <>Esqueceu sua senha? <button data-testid="forgot-password-button" type="button" onClick={() => switchMode("forgot")}>Recupere aqui</button>
            <br />Novo por aqui? <button data-testid="switch-register-button" type="button" onClick={() => switchMode("register")}>Criar conta</button>
            <br /><a data-testid="go-to-admin-login" href="/admin" className="admin-link">Você é da equipe? Acesse o painel administrativo</a></>
          )}
          {mode === "register" && (
            <>Já tem uma conta? <button data-testid="switch-login-button" type="button" onClick={() => switchMode("login")}>Faça login</button></>
          )}
          {(mode === "forgot" || mode === "reset") && (
            <>Lembrou a senha? <button data-testid="switch-login-button" type="button" onClick={() => switchMode("login")}>Voltar para o login</button>
            {mode === "reset" && <><br /><button data-testid="resend-code-button" type="button" onClick={() => switchMode("forgot")}>Enviar novo código</button></>}
            </>
          )}
        </div>
      </form>
    </main>
  );
}

// ============ SHELL ============
function Shell() {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(true);
  const user = JSON.parse(localStorage.getItem("barbearia_user") || "{}");
  return (
    <div className={`app-shell ${dark ? "dark" : "light"}`}>
      <aside className={open ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <img data-testid="sidebar-logo" className="sidebar-logo" src={LOGO} alt="Logo Barbearia Gabriel do Corte" />
          <div><strong>GABRIEL</strong><span>DO CORTE</span></div>
        </div>
        <div className="workspace"><span className="status-dot" /> Operação ativa <ChevronDown size={14} /></div>
        <nav>{nav.map(({ label, icon: Icon, path }) => <NavItem key={path} label={label} icon={Icon} path={path} onClick={() => setOpen(false)} />)}</nav>
        <div className="sidebar-bottom">
          <NavItem label="Configurações" icon={Settings} path="/settings" onClick={() => setOpen(false)} />
          <button data-testid="theme-toggle-button" className="theme-toggle" onClick={() => setDark(!dark)}>{dark ? <Sun size={16} /> : <Moon size={16} />} {dark ? "Modo claro" : "Modo escuro"}</button>
          <div className="user-mini">
            <div className="avatar">{(user.name || "G")[0]}</div>
            <div><strong>{user.name || "Gabriel"}</strong><span>{user.role === "admin" ? "Administrador" : user.role === "barber" ? "Barbeiro" : "Cliente"}</span></div>
            <button data-testid="logout-button" onClick={async () => { try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { } localStorage.clear(); window.location.href = "/admin" }}><LogOut size={16} /></button>
          </div>
        </div>
      </aside>
      <section className="main-area">
        <header className="topbar">
          <button data-testid="mobile-menu-button" className="mobile-menu" onClick={() => setOpen(!open)}><Menu /></button>
          <div className="breadcrumb"><span>Gabriel do Corte</span><b>/</b><strong><CurrentLabel /></strong></div>
          <div className="top-actions">
            <button data-testid="notifications-button" className="icon-btn has-dot"><Bell size={18} /></button>
            <div className="top-avatar">{(user.name || "G")[0]}</div>
          </div>
        </header>
        <main className="content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/appointments" element={<Appointments />} />
            <Route path="/clients" element={<ResourcePage type="clients" />} />
            <Route path="/barbers" element={<ResourcePage type="barbers" />} />
            <Route path="/services" element={<ResourcePage type="services" />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/plans/success" element={<PlansSuccess />} />
            <Route path="/products" element={<ResourcePage type="products" />} />
            <Route path="/cash" element={<Cash />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </section>
    </div>
  );
}

function NavItem({ label, icon: Icon, path, onClick }) {
  const loc = useLocation();
  const nav = useNavigate();
  const active = loc.pathname === path;
  return (
    <a data-testid={`nav-${path.slice(1) || "dashboard"}-link`} className={active ? "nav-item active" : "nav-item"}
       href={path} onClick={(e) => { e.preventDefault(); nav(path); onClick?.(); }}>
      <Icon size={18} /><span>{label}</span>
    </a>
  );
}

function CurrentLabel() {
  const loc = useLocation();
  const match = nav.find(x => x.path === loc.pathname);
  if (match) return match.label;
  if (loc.pathname === "/settings") return "Configurações";
  return "Painel";
}

function Header({ eyebrow, title, subtitle, action, onAction, testId }) {
  return (
    <div className="page-head">
      <div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p className="muted">{subtitle}</p></div>
      {action && <button data-testid={testId || "primary-add-button"} className="gold-btn" onClick={onAction}><Plus size={17} /> {action}</button>}
    </div>
  );
}

const WEEK_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
function DaysPicker({ value = [], onChange, testid }) {
  const arr = Array.isArray(value) ? value : [];
  const toggle = d => {
    const has = arr.includes(d);
    onChange(has ? arr.filter(x => x !== d) : [...arr, d]);
  };
  return (
    <div className="days-picker" data-testid={testid}>
      {WEEK_LABELS.map((label, i) => (
        <button key={i} type="button" data-testid={`${testid}-${i}`} className={`day-chip ${arr.includes(i) ? "on" : ""}`} onClick={() => toggle(i)}>{label}</button>
      ))}
    </div>
  );
}

// ============ DASHBOARD ============
function Dashboard() {
  const [data, setData] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  useEffect(() => {
    axios.get(`${API}/dashboard`, { headers: auth() }).then(r => setData(r.data)).catch(() => { });
    axios.get(`${API}/dashboard/upcoming`, { headers: auth() }).then(r => setUpcoming(r.data)).catch(() => { });
  }, []);
  const stats = [
    ["Agendamentos hoje", data.appointments_today || 0, "Hoje", CalendarDays],
    ["Faturamento", money(data.revenue || 0), `${money(data.revenue_today || 0)} hoje`, CircleDollarSign],
    ["Clientes", data.clients || 0, "Base cadastrada", Users],
    ["Planos ativos", data.plans || 0, "Assinaturas em dia", WalletCards],
  ];
  const weekMax = Math.max(1, ...(data.week || [1]));
  return (
    <>
      <Header eyebrow={new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" }).toUpperCase()} title="Bom dia, Gabriel." subtitle="Aqui está o resumo da sua operação hoje." />
      <div className="stat-grid">
        {stats.map(([name, value, note, Icon]) => (
          <div className="stat-card" data-testid={`stat-${name.toLowerCase().replaceAll(" ", "-")}`} key={name}>
            <div className="stat-top"><span>{name}</span><Icon size={18} /></div>
            <strong>{value}</strong><small>{note}</small>
          </div>
        ))}
      </div>
      <div className="dashboard-grid">
        <section className="panel performance">
          <div className="panel-head"><div><h3>Performance semanal</h3><p className="muted">Faturamento acumulado</p></div>
            <select data-testid="performance-period-select"><option>Últimos 7 dias</option><option>Este mês</option></select>
          </div>
          <div className="chart">
            <div className="chart-y"><span>{money(weekMax)}</span><span>{money(weekMax * 0.66)}</span><span>{money(weekMax * 0.33)}</span><span>R$ 0</span></div>
            <div className="bars">
              {(data.week || [48, 62, 54, 78, 71, 88, 82]).map((v, i) => (
                <div className="bar-col" key={i}><div className="bar" style={{ height: `${Math.max(6, (v / weekMax) * 100)}%` }} /><span>{["S", "T", "Q", "Q", "S", "S", "D"][i]}</span></div>
              ))}
            </div>
          </div>
        </section>
        <section className="panel next-panel">
          <div className="panel-head"><div><h3>Próximos horários</h3><p className="muted">Agenda de hoje</p></div>
            <a data-testid="view-calendar-link" href="/appointments" className="text-link">Ver agenda ↗</a>
          </div>
          {upcoming.length === 0 && <div className="empty">Sem agendamentos futuros.</div>}
          {upcoming.slice(0, 4).map(a => (
            <div className="booking" data-testid={`booking-${a.id}`} key={a.id}>
              <time>{a.time}</time>
              <div className="avatar avatar-sm">{(a.client_name || "?")[0]}</div>
              <div><strong>{a.client_name}</strong><span>{a.service_name} · {a.barber_name}</span></div>
              <b>{a.status === "confirmed" ? "Confirmado" : a.status}</b>
            </div>
          ))}
        </section>
      </div>
    </>
  );
}

// ============ APPOINTMENTS ============
function Appointments() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [subs, setSubs] = useState([]);
  const [show, setShow] = useState(false);
  const [step, setStep] = useState("form"); // form | review
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ client_id: "", barber_id: "", service_id: "", date: new Date().toISOString().slice(0, 10), time: "", use_plan_subscription_id: "" });
  const [slots, setSlots] = useState([]);
  const [slotsInfo, setSlotsInfo] = useState({ closed_day: false });
  const [error, setError] = useState("");
  const [completing, setCompleting] = useState(null); // appointment being completed
  const [completePayment, setCompletePayment] = useState("pix");

  const load = () => axios.get(`${API}/appointments`, { headers: auth() }).then(r => setItems(r.data)).catch(() => { });
  useEffect(() => {
    load();
    axios.get(`${API}/clients`, { headers: auth() }).then(r => setClients(r.data));
    axios.get(`${API}/barbers`, { headers: auth() }).then(r => setBarbers(r.data));
    axios.get(`${API}/services`, { headers: auth() }).then(r => setServices(r.data));
    axios.get(`${API}/subscriptions`, { headers: auth() }).then(r => setSubs(r.data)).catch(() => setSubs([]));
  }, []);
  useEffect(() => {
    if (!form.date || !form.barber_id) { setSlots([]); return; }
    const qp = new URLSearchParams({ date_str: form.date, barber_id: form.barber_id });
    if (form.service_id) qp.set("service_id", form.service_id);
    axios.get(`${API}/appointments/slots?${qp}`, { headers: auth() })
      .then(r => { setSlots(r.data.available); setSlotsInfo(r.data); })
      .catch(() => { setSlots([]); setSlotsInfo({ closed_day: false }); });
  }, [form.date, form.barber_id, form.service_id]);

  const clientObj = clients.find(x => x.id === form.client_id);
  const barberObj = barbers.find(x => x.id === form.barber_id);
  const serviceObj = services.find(x => x.id === form.service_id);
  const availableSubs = subs.filter(s => s.client_id === form.client_id && s.status === "active");

  const goReview = e => {
    e.preventDefault(); setError("");
    if (!form.time) { setError("Escolha um horário"); return; }
    setStep("review");
  };

  const confirmBooking = async () => {
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/appointments`, {
        client_id: form.client_id,
        barber_id: form.barber_id,
        service_id: form.service_id,
        date: form.date, time: form.time,
        use_plan_subscription_id: form.use_plan_subscription_id || null,
      }, { headers: auth() });
      setShow(false); setStep("form");
      setForm({ client_id: "", barber_id: "", service_id: "", date: new Date().toISOString().slice(0, 10), time: "", use_plan_subscription_id: "" });
      load();
    } catch (e) { setError(e.response?.data?.detail || "Erro ao criar agendamento"); }
    setBusy(false);
  };

  const cancel = async id => {
    if (!window.confirm("Cancelar este agendamento?")) return;
    try { await axios.put(`${API}/appointments/${id}`, { data: { status: "cancelled" } }, { headers: auth() }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Erro"); }
  };
  const remove = async id => {
    if (!window.confirm("Excluir este agendamento definitivamente?")) return;
    try { await axios.delete(`${API}/appointments/${id}`, { headers: auth() }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Erro"); }
  };
  const openComplete = a => { setCompleting(a); setCompletePayment("pix"); };
  const confirmComplete = async () => {
    try {
      const r = await axios.post(`${API}/appointments/${completing.id}/complete`, { payment_method: completePayment }, { headers: auth() });
      alert(`Atendimento finalizado. Comissão: ${money(r.data.commission)} · Pontos: +${r.data.points_awarded}`);
      setCompleting(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Erro"); }
  };

  const filtered = items.filter(x => JSON.stringify(x).toLowerCase().includes(q.toLowerCase()));
  const statusLabel = s => ({ confirmed: "Confirmado", cancelled: "Cancelado", completed: "Concluído" }[s] || s);
  return (
    <>
      <Header eyebrow="AGENDA" title="Agendamentos" subtitle="Controle a agenda da barbearia em tempo real." action="Novo agendamento" onAction={() => { setShow(true); setStep("form"); setError(""); }} testId="appointments-add-button" />
      <div className="toolbar">
        <div className="search-field"><Search size={17} /><input data-testid="appointments-search-input" placeholder="Pesquisar cliente, barbeiro..." value={q} onChange={e => setQ(e.target.value)} /></div>
        <span className="result-count">{filtered.length} registros</span>
      </div>
      <div className="table-panel">
        <table>
          <thead><tr><th>Cliente</th><th>Serviço</th><th>Barbeiro</th><th>Data / Hora</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {filtered.map(a => (
              <tr key={a.id} data-testid={`appointment-row-${a.id}`}>
                <td><div className="table-person"><div className="avatar avatar-sm">{(a.client_name || "?")[0]}</div><div><strong>{a.client_name}</strong><span>{money(a.price)} · {a.duration || 30}min</span></div></div></td>
                <td>{a.service_name}</td>
                <td>{a.barber_name}</td>
                <td>{a.date} · <strong>{a.time}</strong></td>
                <td><span className={`pill ${a.status === "cancelled" ? "danger" : a.status === "completed" ? "muted" : "active"}`}>{statusLabel(a.status)}</span></td>
                <td>
                  <div className="row-actions">
                    {a.status === "confirmed" && <button data-testid={`appointment-complete-${a.id}`} className="row-edit gold" onClick={() => openComplete(a)}>Concluir</button>}
                    {a.status === "confirmed" && <button data-testid={`appointment-cancel-${a.id}`} className="row-edit" onClick={() => cancel(a.id)}>Cancelar</button>}
                    <button data-testid={`appointment-delete-${a.id}`} className="delete-btn" onClick={() => remove(a.id)}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty">Nenhum agendamento encontrado.</div>}
      </div>
      {show && step === "form" && (
        <div className="modal-backdrop">
          <form data-testid="appointment-create-form" className="modal wide-modal" onSubmit={goReview}>
            <button data-testid="modal-close-button" type="button" className="modal-close" onClick={() => setShow(false)}><X /></button>
            <p className="eyebrow">NOVO AGENDAMENTO · Passo 1 de 2</p>
            <h2>Marcar horário</h2>
            <div className="modal-grid">
              <label>Cliente
                <select data-testid="appointment-client-select" value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value, use_plan_subscription_id: "" })} required>
                  <option value="">Selecione</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label>Barbeiro
                <select data-testid="appointment-barber-select" value={form.barber_id} onChange={e => setForm({ ...form, barber_id: e.target.value, time: "" })} required>
                  <option value="">Selecione</option>
                  {barbers.map(b => <option key={b.id} value={b.id}>{b.name} · {b.start_time || "09:00"}–{b.end_time || "19:30"}</option>)}
                </select>
              </label>
              <label>Serviço
                <select data-testid="appointment-service-select" value={form.service_id} onChange={e => setForm({ ...form, service_id: e.target.value, time: "" })} required>
                  <option value="">Selecione</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name} · {money(s.price)} · {s.duration}min</option>)}
                </select>
              </label>
              <label>Data
                <input data-testid="appointment-date-input" type="date" value={form.date} min={new Date().toISOString().slice(0, 10)} onChange={e => setForm({ ...form, date: e.target.value, time: "" })} required />
              </label>
              {availableSubs.length > 0 && (
                <label className="full-col">Usar plano do cliente (opcional)
                  <select data-testid="appointment-plan-select" value={form.use_plan_subscription_id} onChange={e => setForm({ ...form, use_plan_subscription_id: e.target.value })}>
                    <option value="">Pagar avulso</option>
                    {availableSubs.map(s => <option key={s.id} value={s.id}>{s.plan_id}</option>)}
                  </select>
                </label>
              )}
            </div>
            <label>Horário disponível</label>
            {slotsInfo.closed_day ? <div className="empty small">Barbeiro não atende nesta data.</div> : null}
            <div className="slot-grid">
              {!slotsInfo.closed_day && slots.length === 0 && <span className="muted">Selecione barbeiro, serviço e data para ver os horários.</span>}
              {slots.map(s => (
                <button key={s} type="button" data-testid={`slot-${s}`} className={`slot ${form.time === s ? "active" : ""}`} onClick={() => setForm({ ...form, time: s })}>{s}</button>
              ))}
            </div>
            {error && <div className="error-msg">{error}</div>}
            <button data-testid="appointment-review-button" className="gold-btn full" disabled={!form.time}>Revisar antes de confirmar</button>
          </form>
        </div>
      )}
      {show && step === "review" && (
        <div className="modal-backdrop">
          <div className="modal wide-modal">
            <button type="button" className="modal-close" onClick={() => setShow(false)}><X /></button>
            <p className="eyebrow">NOVO AGENDAMENTO · Passo 2 de 2</p>
            <h2>Confirme os dados</h2>
            <ul className="review-list">
              <li><span>Cliente</span><strong>{clientObj?.name}</strong></li>
              <li><span>Telefone</span><strong>{clientObj?.phone || "—"}</strong></li>
              <li><span>Barbeiro</span><strong>{barberObj?.name}</strong></li>
              <li><span>Serviço</span><strong>{serviceObj?.name} · {serviceObj?.duration}min</strong></li>
              <li><span>Data e horário</span><strong>{form.date} · {form.time}</strong></li>
              <li><span>Valor</span><strong>{form.use_plan_subscription_id ? "Coberto pelo plano" : money(serviceObj?.price || 0)}</strong></li>
            </ul>
            {error && <div className="error-msg">{error}</div>}
            <div className="review-actions">
              <button data-testid="appointment-back-button" type="button" className="ghost-btn" onClick={() => setStep("form")}>Voltar</button>
              <button data-testid="appointment-save-button" className="gold-btn" disabled={busy} onClick={confirmBooking}>{busy ? "Salvando..." : "Confirmar agendamento"}</button>
            </div>
          </div>
        </div>
      )}
      {completing && (
        <div className="modal-backdrop">
          <div className="modal">
            <button type="button" className="modal-close" onClick={() => setCompleting(null)}><X /></button>
            <p className="eyebrow">CONCLUIR ATENDIMENTO</p>
            <h2>{completing.client_name}</h2>
            <p className="muted">{completing.service_name} · {money(completing.price)}</p>
            {completing.used_subscription_id ? (
              <p className="positive"><Check size={14} /> Pagamento coberto pelo plano assinante</p>
            ) : (
              <label>Forma de pagamento
                <select data-testid="complete-payment-select" value={completePayment} onChange={e => setCompletePayment(e.target.value)}>
                  <option value="pix">PIX</option>
                  <option value="card">Cartão</option>
                  <option value="cash">Dinheiro</option>
                </select>
              </label>
            )}
            <button data-testid="confirm-complete-button" className="gold-btn full" onClick={confirmComplete}>Finalizar e lançar no caixa</button>
          </div>
        </div>
      )}
    </>
  );
}

// ============ RESOURCE PAGE (generic CRUD) ============
const RESOURCE_META = {
  clients: {
    title: "Clientes", subtitle: "Relacionamento e fidelidade em um só lugar", singular: "cliente",
    fields: [
      { key: "name", label: "Nome", type: "text", required: true },
      { key: "phone", label: "Telefone", type: "text" },
      { key: "email", label: "E-mail", type: "email" },
      { key: "points", label: "Pontos fidelidade", type: "number" },
      { key: "preferences", label: "Preferências", type: "textarea" },
    ],
    columns: (item) => [
      { primary: item.name, secondary: item.phone || item.email },
      item.email || "-",
      item.points ? `${item.points} pts` : "0 pts",
    ]
  },
  barbers: {
    title: "Barbeiros", subtitle: "Equipe, jornada e folgas", singular: "barbeiro",
    fields: [
      { key: "name", label: "Nome", type: "text", required: true },
      { key: "specialty", label: "Especialidade", type: "text" },
      { key: "phone", label: "Telefone", type: "text" },
      { key: "commission", label: "Comissão (%)", type: "number" },
      { key: "start_time", label: "Início da jornada", type: "time" },
      { key: "end_time", label: "Fim da jornada", type: "time" },
      { key: "working_days", label: "Dias que trabalha", type: "days" },
    ],
    columns: (item) => [
      { primary: item.name, secondary: item.specialty },
      `${item.start_time || "09:00"}–${item.end_time || "19:30"}`,
      `${item.commission || 0}%`,
    ]
  },
  services: {
    title: "Serviços", subtitle: "Seu catálogo de experiências", singular: "serviço",
    fields: [
      { key: "name", label: "Nome", type: "text", required: true },
      { key: "category", label: "Categoria", type: "text" },
      { key: "price", label: "Preço (R$)", type: "number", required: true },
      { key: "duration", label: "Duração (min)", type: "number" },
      { key: "description", label: "Descrição", type: "textarea" },
    ],
    columns: (item) => [
      { primary: item.name, secondary: item.description },
      item.category || "-",
      money(item.price),
    ]
  },
  products: {
    title: "Produtos e estoque", subtitle: "Controle de produtos e vendas", singular: "produto",
    fields: [
      { key: "name", label: "Nome", type: "text", required: true },
      { key: "category", label: "Categoria", type: "text" },
      { key: "price", label: "Preço (R$)", type: "number", required: true },
      { key: "stock", label: "Estoque", type: "number", required: true },
      { key: "description", label: "Descrição", type: "textarea" },
    ],
    columns: (item) => [
      { primary: item.name, secondary: item.description },
      `${item.stock ?? 0} un.`,
      money(item.price),
    ]
  }
};

function ResourcePage({ type }) {
  const meta = RESOURCE_META[type];
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const load = () => axios.get(`${API}/${type}`, { headers: auth() }).then(r => setItems(r.data)).catch(() => { });
  useEffect(() => { load(); setForm({}); }, [type]);
  const openCreate = () => { setEditing(null); setForm({}); setShow(true); };
  const openEdit = (item) => { setEditing(item); setForm(item); setShow(true); };
  const save = async e => {
    e.preventDefault(); setBusy(true);
    const payload = { ...form };
    meta.fields.forEach(f => { if (f.type === "number" && payload[f.key] !== undefined) payload[f.key] = Number(payload[f.key]); });
    try {
      if (editing) await axios.put(`${API}/${type}/${editing.id}`, { data: payload }, { headers: auth() });
      else await axios.post(`${API}/${type}`, { data: { ...payload, status: "active" } }, { headers: auth() });
      setShow(false); setForm({}); setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Erro ao salvar"); }
    setBusy(false);
  };
  const remove = async id => {
    if (!window.confirm("Excluir este registro? Esta ação não pode ser desfeita.")) return;
    try { await axios.delete(`${API}/${type}/${id}`, { headers: auth() }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Erro ao excluir"); }
  };
  const filtered = useMemo(() => items.filter(x => JSON.stringify(x).toLowerCase().includes(q.toLowerCase())), [items, q]);
  return (
    <>
      <Header eyebrow="GESTÃO" title={meta.title} subtitle={meta.subtitle} action={`Adicionar ${meta.singular}`} onAction={openCreate} testId={`${type}-add-button`} />
      <div className="toolbar">
        <div className="search-field"><Search size={17} /><input data-testid={`${type}-search-input`} placeholder={`Pesquisar ${meta.singular}...`} value={q} onChange={e => setQ(e.target.value)} /></div>
        <span className="result-count">{filtered.length} registros</span>
      </div>
      <div className="table-panel">
        <table>
          <thead><tr><th>{meta.singular}</th><th>Detalhes</th><th>Valor</th><th></th></tr></thead>
          <tbody>
            {filtered.map(item => {
              const cols = meta.columns(item);
              return (
                <tr key={item.id} data-testid={`${type}-row-${item.id}`}>
                  <td><div className="table-person"><div className="avatar avatar-sm">{(cols[0].primary || "?")[0]}</div><div><strong>{cols[0].primary}</strong><span>{cols[0].secondary || "Cadastro ativo"}</span></div></div></td>
                  <td>{cols[1]}</td>
                  <td><strong>{cols[2]}</strong></td>
                  <td>
                    <div className="row-actions">
                      <button data-testid={`${type}-edit-${item.id}`} className="row-edit" onClick={() => openEdit(item)}>Editar</button>
                      <button data-testid={`${type}-delete-${item.id}`} className="delete-btn" onClick={() => remove(item.id)}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty">Nenhum registro encontrado.</div>}
      </div>
      {show && (
        <div className="modal-backdrop">
          <form data-testid={`${type}-create-form`} className="modal wide-modal" onSubmit={save}>
            <button data-testid="modal-close-button" type="button" className="modal-close" onClick={() => setShow(false)}><X /></button>
            <p className="eyebrow">{editing ? "EDITAR" : "NOVO CADASTRO"}</p>
            <h2>{editing ? `Editar ${meta.singular}` : `Adicionar ${meta.singular}`}</h2>
            <div className="modal-grid">
              {meta.fields.map(f => (
                <label key={f.key} className={(f.type === "textarea" || f.type === "days") ? "full-col" : ""}>{f.label}
                  {f.type === "textarea"
                    ? <textarea data-testid={`${type}-${f.key}-input`} rows={3} value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                    : f.type === "days"
                    ? <DaysPicker testid={`${type}-${f.key}-input`} value={form[f.key] || [1, 2, 3, 4, 5, 6]} onChange={v => setForm({ ...form, [f.key]: v })} />
                    : <input data-testid={`${type}-${f.key}-input`} type={f.type} value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })} required={f.required} />}
                </label>
              ))}
            </div>
            <button data-testid={`${type}-save-button`} className="gold-btn full" disabled={busy}>{busy ? "Salvando..." : "Salvar cadastro"}</button>
          </form>
        </div>
      )}
    </>
  );
}

// ============ PLANS (with Stripe) ============
function Plans() {
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [subShow, setSubShow] = useState(null); // plan to subscribe
  const [selectedClient, setSelectedClient] = useState("");
  const load = () => axios.get(`${API}/plans`, { headers: auth() }).then(r => setPlans(r.data)).catch(() => { });
  useEffect(() => {
    load();
    axios.get(`${API}/clients`, { headers: auth() }).then(r => setClients(r.data));
  }, []);
  const save = async e => {
    e.preventDefault(); setBusy(true);
    const payload = { ...form, price: Number(form.price || 0) };
    try {
      if (editing) await axios.put(`${API}/plans/${editing.id}`, { data: payload }, { headers: auth() });
      else await axios.post(`${API}/plans`, { data: { ...payload, status: "active" } }, { headers: auth() });
      setShow(false); setForm({}); setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Erro ao salvar"); }
    setBusy(false);
  };
  const remove = async id => {
    if (!window.confirm("Excluir este plano?")) return;
    await axios.delete(`${API}/plans/${id}`, { headers: auth() });
    load();
  };
  const subscribe = async (plan) => {
    if (!plan.lookup_key) { alert("Este plano não possui código de assinatura vinculado. Configure o lookup_key."); return; }
    if (!selectedClient) { alert("Selecione o cliente."); return; }
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
        plan_id: plan.id,
        client_id: selectedClient,
      }, { headers: auth() });
      window.location.href = r.data.checkout_url;
    } catch (e) { alert(e.response?.data?.detail || "Erro ao iniciar checkout"); }
  };
  return (
    <>
      <Header eyebrow="ASSINATURAS" title="Planos mensais" subtitle="Assinaturas que fazem o cliente voltar." action="Adicionar plano" onAction={() => { setEditing(null); setForm({}); setShow(true); }} testId="plans-add-button" />
      <div className="plan-grid">
        {plans.map(p => (
          <div className="plan-card" data-testid={`plan-card-${p.id}`} key={p.id}>
            <div className="plan-head"><Sparkles size={16} /><span>{p.name?.toUpperCase()}</span></div>
            <h2>{money(p.price)}<small>/mês</small></h2>
            <p className="muted">{p.description}</p>
            <ul>{(p.features || "").split(",").map((f, i) => f.trim() && <li key={i}><Check size={14} /> {f.trim()}</li>)}</ul>
            <div className="plan-actions">
              <button data-testid={`plan-subscribe-${p.id}`} className="gold-btn" onClick={() => setSubShow(p)}>Assinar plano</button>
              <button data-testid={`plan-edit-${p.id}`} className="ghost-btn" onClick={() => { setEditing(p); setForm(p); setShow(true); }}>Editar</button>
              <button data-testid={`plan-delete-${p.id}`} className="delete-btn icon-only" onClick={() => remove(p.id)}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {plans.length === 0 && <div className="empty">Nenhum plano cadastrado.</div>}
      </div>
      {show && (
        <div className="modal-backdrop">
          <form data-testid="plan-create-form" className="modal wide-modal" onSubmit={save}>
            <button type="button" className="modal-close" onClick={() => setShow(false)}><X /></button>
            <p className="eyebrow">{editing ? "EDITAR" : "NOVO"}</p>
            <h2>{editing ? "Editar plano" : "Novo plano"}</h2>
            <div className="modal-grid">
              <label>Nome<input data-testid="plan-name-input" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} required /></label>
              <label>Preço mensal (R$)<input data-testid="plan-price-input" type="number" step="0.01" value={form.price || ""} onChange={e => setForm({ ...form, price: e.target.value })} required /></label>
              <label>Usos por mês (0 = ilimitado)<input data-testid="plan-uses-input" type="number" value={form.uses_per_month ?? ""} onChange={e => setForm({ ...form, uses_per_month: e.target.value })} /></label>
              <label>Lookup key Stripe<input data-testid="plan-lookup-input" value={form.lookup_key || ""} onChange={e => setForm({ ...form, lookup_key: e.target.value })} placeholder="plan_basico_monthly" /></label>
              <label className="full-col">Descrição<input data-testid="plan-description-input" value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} /></label>
              <label className="full-col">Benefícios (separados por vírgula)<input data-testid="plan-features-input" value={form.features || ""} onChange={e => setForm({ ...form, features: e.target.value })} /></label>
            </div>
            <button className="gold-btn full" disabled={busy}>{busy ? "Salvando..." : "Salvar"}</button>
          </form>
        </div>
      )}
      {subShow && (
        <div className="modal-backdrop">
          <div className="modal">
            <button type="button" className="modal-close" onClick={() => setSubShow(null)}><X /></button>
            <p className="eyebrow">ASSINATURA</p>
            <h2>Assinar {subShow.name}</h2>
            <p className="muted">Selecione o cliente que assinará o plano de {money(subShow.price)}/mês.</p>
            <label>Cliente
              <select data-testid="subscribe-client-select" value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
                <option value="">Selecione</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <button data-testid="subscribe-confirm-button" className="gold-btn full" onClick={() => subscribe(subShow)}>Ir para o pagamento (Stripe)</button>
            <p className="muted small">Modo teste · use o cartão 4242 4242 4242 4242</p>
          </div>
        </div>
      )}
    </>
  );
}

function PlansSuccess() {
  const location = useLocation();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) { nav("/plans"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sid}`);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (attempts > 10) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch { setStatus("error"); }
    };
    poll();
  }, [location.search, nav]);
  return (
    <div className="success-page">
      <Header eyebrow="PAGAMENTO" title={status === "paid" ? "Assinatura ativada!" : status === "checking" ? "Confirmando pagamento..." : "Aguardando confirmação"} subtitle="Você pode acompanhar as assinaturas ativas no painel." />
      <div className="panel">
        {status === "paid" && <p className="positive"><Check size={16} /> Plano ativado com sucesso. Um novo registro foi criado em assinantes.</p>}
        {status === "checking" && <p className="muted">Consultando o Stripe...</p>}
        {status === "timeout" && <p className="muted">Ainda não confirmamos o pagamento. Aguarde a atualização do webhook.</p>}
        {status === "error" && <p className="muted">Não foi possível recuperar o status.</p>}
        <button data-testid="return-plans-button" className="gold-btn" onClick={() => nav("/plans")}>Voltar para planos</button>
      </div>
    </div>
  );
}

// ============ CASH ============
function Cash() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: "income", method: "pix", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
  const load = () => axios.get(`${API}/transactions`, { headers: auth() }).then(r => setItems(r.data)).catch(() => { });
  useEffect(() => { load(); }, []);
  const save = async e => {
    e.preventDefault(); setBusy(true);
    try {
      await axios.post(`${API}/transactions`, { data: { ...form, amount: Number(form.amount) } }, { headers: auth() });
      setShow(false); setForm({ type: "income", method: "pix", amount: "", description: "", date: new Date().toISOString().slice(0, 10) }); load();
    } catch (e) { alert(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };
  const remove = async id => {
    if (!window.confirm("Excluir movimentação?")) return;
    await axios.delete(`${API}/transactions/${id}`, { headers: auth() }); load();
  };
  const today = new Date().toISOString().slice(0, 10);
  const todayItems = items.filter(x => x.date === today);
  const income = todayItems.filter(x => x.type === "income").reduce((a, b) => a + (b.amount || 0), 0);
  const expense = todayItems.filter(x => x.type === "expense").reduce((a, b) => a + (b.amount || 0), 0);
  return (
    <>
      <Header eyebrow="FINANCEIRO" title="Caixa" subtitle="Acompanhe entradas, saídas e o fechamento do dia." action="Nova movimentação" onAction={() => setShow(true)} testId="cash-add-button" />
      <div className="stat-grid">
        <div className="stat-card"><div className="stat-top"><span>Saldo do dia</span><CircleDollarSign size={18} /></div><strong>{money(income - expense)}</strong><small className="positive">{todayItems.length} movimentações</small></div>
        <div className="stat-card"><div className="stat-top"><span>Entradas</span></div><strong>{money(income)}</strong><small>{todayItems.filter(x => x.type === "income").length} transações</small></div>
        <div className="stat-card"><div className="stat-top"><span>Saídas</span></div><strong>{money(expense)}</strong><small>{todayItems.filter(x => x.type === "expense").length} despesas</small></div>
      </div>
      <div className="table-panel">
        <div className="panel-head"><div><h3>Movimentações recentes</h3><p className="muted">{new Date().toLocaleDateString("pt-BR")}</p></div></div>
        {items.slice(0, 20).map((x, i) => (
          <div className="activity-row" data-testid={`cash-row-${x.id}`} key={x.id}>
            <div className={`activity-icon ${x.type === "expense" ? "red" : "green"}`}>{x.type === "expense" ? "−" : "+"}</div>
            <div><strong>{x.description}</strong><span>{(x.method || "").toUpperCase()} · {x.date}</span></div>
            <b className={x.type === "expense" ? "negative" : ""}>{x.type === "expense" ? "− " : "+ "}{money(x.amount)}</b>
            <button className="delete-btn" data-testid={`cash-delete-${x.id}`} onClick={() => remove(x.id)}><Trash2 size={13} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="empty">Nenhuma movimentação registrada.</div>}
      </div>
      {show && (
        <div className="modal-backdrop">
          <form data-testid="cash-create-form" className="modal wide-modal" onSubmit={save}>
            <button type="button" className="modal-close" onClick={() => setShow(false)}><X /></button>
            <p className="eyebrow">NOVA MOVIMENTAÇÃO</p>
            <h2>Registrar caixa</h2>
            <div className="modal-grid">
              <label>Tipo
                <select data-testid="cash-type-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  <option value="income">Entrada</option>
                  <option value="expense">Saída</option>
                </select>
              </label>
              <label>Forma de pagamento
                <select data-testid="cash-method-select" value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
                  <option value="pix">PIX</option>
                  <option value="card">Cartão</option>
                  <option value="cash">Dinheiro</option>
                </select>
              </label>
              <label>Valor (R$)<input data-testid="cash-amount-input" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></label>
              <label>Data<input data-testid="cash-date-input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required /></label>
              <label className="full-col">Descrição<input data-testid="cash-description-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></label>
            </div>
            <button data-testid="cash-save-button" className="gold-btn full" disabled={busy}>{busy ? "Salvando..." : "Registrar"}</button>
          </form>
        </div>
      )}
    </>
  );
}

// ============ REPORTS ============
function Reports() {
  const [tx, setTx] = useState([]);
  const [appts, setAppts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/transactions`, { headers: auth() }).then(r => setTx(r.data));
    axios.get(`${API}/appointments`, { headers: auth() }).then(r => setAppts(r.data));
  }, []);
  const income = tx.filter(x => x.type === "income").reduce((a, b) => a + (b.amount || 0), 0);
  const serviceCount = {};
  appts.forEach(a => { if (a.service_name) serviceCount[a.service_name] = (serviceCount[a.service_name] || 0) + 1; });
  const topService = Object.entries(serviceCount).sort((a, b) => b[1] - a[1])[0] || ["Corte clássico", 0];
  const clientCount = {};
  appts.forEach(a => { if (a.client_name) clientCount[a.client_name] = (clientCount[a.client_name] || 0) + 1; });
  const topClient = Object.entries(clientCount).sort((a, b) => b[1] - a[1])[0] || ["-", 0];
  const exportCSV = () => {
    const rows = [["Data", "Descrição", "Tipo", "Valor", "Método"], ...tx.map(x => [x.date, x.description, x.type, x.amount, x.method])];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "relatorio-caixa.csv"; a.click();
  };
  return (
    <>
      <Header eyebrow="INTELIGÊNCIA" title="Relatórios" subtitle="Uma leitura clara para decisões melhores." action="Exportar CSV" onAction={exportCSV} testId="export-report-button" />
      <div className="report-grid">
        <div className="report-card"><span>↗</span><p>Faturamento total</p><strong>{money(income)}</strong><small>{tx.filter(x => x.type === "income").length} transações</small></div>
        <div className="report-card"><span>◈</span><p>Serviço mais vendido</p><strong>{topService[0]}</strong><small>{topService[1]} atendimentos</small></div>
        <div className="report-card"><span>★</span><p>Cliente mais frequente</p><strong>{topClient[0]}</strong><small>{topClient[1]} visitas</small></div>
      </div>
      <div className="panel insight-panel">
        <div>
          <p className="eyebrow">INSIGHT DO MÊS</p>
          <h2>Seu horário nobre é entre 17h e 19h</h2>
          <p className="muted">Considere abrir uma agenda extra nesse período. A procura está 32% acima da média.</p>
        </div>
        <div className="insight-number">32<span>%</span></div>
      </div>
    </>
  );
}

// ============ NOTIFICATIONS (Fila real de WhatsApp) ============
function Notifications() {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/messages`, { headers: auth() }).then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const markSent = async id => {
    try { await axios.post(`${API}/messages/${id}/mark-sent`, {}, { headers: auth() }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Erro"); }
  };
  const typeLabel = t => ({ confirmation: "Confirmação", reminder: "Lembrete" }[t] || t);
  const statusLabel = s => ({ sent: "Enviado (Twilio)", queued: "Aguardando envio", sent_manual: "Enviado manualmente", error: "Erro" }[s] || s);
  return (
    <>
      <Header eyebrow="COMUNICAÇÃO" title="Notificações WhatsApp" subtitle="Toda mensagem gerada por confirmações e lembretes aparece aqui." />
      <div className="notice-banner">
        <MessageSquare size={16} />
        <div>
          <strong>Envio automático via Twilio</strong>
          <span>Configure TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN e TWILIO_WHATSAPP_FROM no .env para envio direto. Enquanto isso, clique em "Enviar via wa.me" para disparar manualmente.</span>
        </div>
      </div>
      <div className="notif-grid">
        {items.length === 0 && <div className="empty">Nenhuma mensagem ainda. Faça um agendamento para ver a confirmação aqui.</div>}
        {items.map(m => (
          <div className="notif-card" key={m.id} data-testid={`notif-${m.id}`}>
            <div className="notif-tag">{typeLabel(m.type)} · {statusLabel(m.status)}</div>
            <h3>{m.phone || "sem telefone"}</h3>
            <p className="wa-bubble">{m.body}</p>
            <div className="notif-actions">
              {m.wa_me && m.status !== "sent_manual" && (
                <a data-testid={`notif-send-${m.id}`} className="gold-btn" href={m.wa_me} target="_blank" rel="noreferrer" onClick={() => markSent(m.id)}>Enviar via wa.me</a>
              )}
              {m.status === "sent_manual" && <span className="positive"><Check size={14} /> Marcado como enviado</span>}
              {m.status === "sent" && <span className="positive"><Check size={14} /> Enviado automaticamente</span>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ============ SETTINGS ============
function SettingsPage() {
  const [wa, setWa] = useState(true);
  const [stock, setStock] = useState(true);
  return (
    <>
      <Header eyebrow="SISTEMA" title="Configurações" subtitle="A identidade e as regras da sua barbearia." />
      <div className="settings-grid">
        <div className="panel settings-card">
          <img data-testid="settings-logo" className="settings-logo" src={LOGO} alt="Logo da barbearia" />
          <h3>Identidade da marca</h3>
          <p className="muted">BARBEARIA GABRIEL DO CORTE</p>
          <button data-testid="change-logo-button" className="ghost-btn">Alterar logo</button>
        </div>
        <div className="panel settings-card">
          <h3>Notificações</h3>
          <div className="setting-row">
            <div><strong>Lembretes por WhatsApp</strong><span>Confirmações e avisos automáticos</span></div>
            <div className={`toggle ${wa ? "on" : ""}`} data-testid="whatsapp-toggle" onClick={() => setWa(!wa)}><i /></div>
          </div>
          <div className="setting-row">
            <div><strong>Alertas de estoque</strong><span>Quando um produto estiver acabando</span></div>
            <div className={`toggle ${stock ? "on" : ""}`} data-testid="stock-toggle" onClick={() => setStock(!stock)}><i /></div>
          </div>
          <div className="integration-note">WhatsApp preparado · conexão pendente</div>
        </div>
        <div className="panel settings-card">
          <h3>Horários de funcionamento</h3>
          <div className="setting-row"><div><strong>Segunda a sexta</strong><span>09h - 20h</span></div></div>
          <div className="setting-row"><div><strong>Sábado</strong><span>09h - 18h</span></div></div>
          <div className="setting-row"><div><strong>Domingo</strong><span>Fechado</span></div></div>
        </div>
        <div className="panel settings-card">
          <h3>Pagamentos</h3>
          <div className="setting-row"><div><strong>Stripe</strong><span>Modo teste · sandbox ativo</span></div><span className="pill active">Conectado</span></div>
          <div className="setting-row"><div><strong>PIX / Dinheiro / Cartão</strong><span>Registro manual no caixa</span></div><span className="pill active">Ativo</span></div>
        </div>
      </div>
    </>
  );
}

// ============ CLIENT AREA (área do cliente logado) ============
function ClientArea() {
  const user = JSON.parse(localStorage.getItem("barbearia_user") || "{}");
  const [myClient, setMyClient] = useState(null);
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [mySubs, setMySubs] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [showBook, setShowBook] = useState(false);
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ barber_id: "", service_id: "", date: new Date().toISOString().slice(0, 10), time: "", use_plan_subscription_id: "" });
  const [slots, setSlots] = useState([]);
  const [slotsInfo, setSlotsInfo] = useState({ closed_day: false });

  const loadClient = () => axios.get(`${API}/me/client`, { headers: auth() }).then(r => setMyClient(r.data)).catch(() => setMyClient(null));
  const loadAppts = () => axios.get(`${API}/me/appointments`, { headers: auth() }).then(r => setAppointments(r.data)).catch(() => setAppointments([]));
  const loadSubs = () => axios.get(`${API}/me/subscriptions`, { headers: auth() }).then(r => setMySubs(r.data)).catch(() => setMySubs([]));
  const load = () => {
    loadClient();
    loadAppts();
    loadSubs();
    axios.get(`${API}/barbers`, { headers: auth() }).then(r => setBarbers(r.data)).catch(() => { });
    axios.get(`${API}/services`, { headers: auth() }).then(r => setServices(r.data)).catch(() => { });
    axios.get(`${API}/plans`, { headers: auth() }).then(r => setPlans(r.data)).catch(() => { });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!form.date || !form.barber_id) { setSlots([]); return; }
    const qp = new URLSearchParams({ date_str: form.date, barber_id: form.barber_id });
    if (form.service_id) qp.set("service_id", form.service_id);
    axios.get(`${API}/appointments/slots?${qp}`, { headers: auth() })
      .then(r => { setSlots(r.data.available); setSlotsInfo(r.data); })
      .catch(() => { setSlots([]); setSlotsInfo({ closed_day: false }); });
  }, [form.date, form.barber_id, form.service_id]);

  const ensureClient = async () => {
    if (myClient) return myClient;
    const r = await axios.post(`${API}/me/client`, {}, { headers: auth() });
    setMyClient(r.data);
    return r.data;
  };

  const barberObj = barbers.find(x => x.id === form.barber_id);
  const serviceObj = services.find(x => x.id === form.service_id);

  const goReview = e => {
    e.preventDefault(); setError("");
    if (!form.time) { setError("Escolha um horário"); return; }
    setStep("review");
  };

  const confirmBooking = async () => {
    setBusy(true); setError("");
    try {
      const c = await ensureClient();
      await axios.post(`${API}/appointments`, {
        client_id: c.id,
        barber_id: form.barber_id,
        service_id: form.service_id,
        date: form.date, time: form.time,
        use_plan_subscription_id: form.use_plan_subscription_id || null,
      }, { headers: auth() });
      setShowBook(false); setStep("form");
      setForm({ barber_id: "", service_id: "", date: new Date().toISOString().slice(0, 10), time: "", use_plan_subscription_id: "" });
      load();
    } catch (e) { setError(e.response?.data?.detail || "Erro ao agendar. Tente novamente."); }
    setBusy(false);
  };

  const cancel = async id => {
    if (!window.confirm("Cancelar este agendamento?")) return;
    try {
      await axios.put(`${API}/appointments/${id}`, { data: { status: "cancelled" } }, { headers: auth() });
      loadAppts();
    } catch (e) { alert(e.response?.data?.detail || "Erro ao cancelar"); }
  };

  const subscribe = async (plan) => {
    if (!plan.lookup_key) { alert("Plano não configurado para pagamento online."); return; }
    try {
      const c = await ensureClient();
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: plan.lookup_key, origin_url: window.location.origin,
        plan_id: plan.id, client_id: c.id,
      }, { headers: auth() });
      window.location.href = r.data.checkout_url;
    } catch (e) { alert(e.response?.data?.detail || "Erro ao iniciar checkout"); }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { }
    localStorage.clear(); window.location.href = "/login";
  };

  return (
    <div className="client-area dark">
      <header className="client-topbar">
        <div className="brand-row">
          <img className="sidebar-logo" src={LOGO} alt="Logo" />
          <div><strong>GABRIEL</strong><span>DO CORTE</span></div>
        </div>
        <div className="client-user">
          <div className="avatar">{(user.name || "?")[0]}</div>
          <div className="client-user-info"><strong>{user.name}</strong><span>Cliente</span></div>
          <button data-testid="client-logout-button" className="icon-btn" onClick={logout}><LogOut size={18} /></button>
        </div>
      </header>
      <main className="content client-content">
        <div className="page-head">
          <div>
            <p className="eyebrow">ÁREA DO CLIENTE</p>
            <h1>Olá, {(user.name || "").split(" ")[0]}.</h1>
            <p className="muted">Agende seu próximo corte e acompanhe seus benefícios.</p>
          </div>
          <button data-testid="client-book-button" className="gold-btn" onClick={() => { setShowBook(true); setStep("form"); setError(""); }}><Plus size={17} /> Agendar corte</button>
        </div>

        <div className="stat-grid">
          <div className="stat-card"><div className="stat-top"><span>Meus agendamentos</span><CalendarDays size={18} /></div><strong>{appointments.filter(a => a.status === "confirmed").length}</strong><small>Ativos</small></div>
          <div className="stat-card"><div className="stat-top"><span>Pontos de fidelidade</span><Sparkles size={18} /></div><strong>{myClient?.points || 0}</strong><small>Trocáveis por benefícios</small></div>
          <div className="stat-card"><div className="stat-top"><span>Última visita</span><Clock3 size={18} /></div><strong>{myClient?.last_visit || "—"}</strong><small>Volte sempre</small></div>
          <div className="stat-card"><div className="stat-top"><span>Planos ativos</span><WalletCards size={18} /></div><strong>{mySubs.length}</strong><small>Suas assinaturas</small></div>
        </div>

        <div className="dashboard-grid client-grid">
          <section className="panel">
            <div className="panel-head"><div><h3>Meus agendamentos</h3><p className="muted">Cancelamento até 2h antes</p></div></div>
            {appointments.length === 0 && <div className="empty">Você ainda não tem agendamentos.</div>}
            {appointments.slice(0, 6).map(a => (
              <div className="booking" data-testid={`client-appointment-${a.id}`} key={a.id}>
                <time>{a.time}</time>
                <div className="avatar avatar-sm">{(a.barber_name || "?")[0]}</div>
                <div><strong>{a.service_name}</strong><span>{a.date} · {a.barber_name}</span></div>
                {a.status === "confirmed" ? (
                  <button data-testid={`client-cancel-${a.id}`} className="row-edit" onClick={() => cancel(a.id)}>Cancelar</button>
                ) : <b className={`pill ${a.status === "cancelled" ? "danger" : "muted"}`}>{a.status === "cancelled" ? "Cancelado" : "Concluído"}</b>}
              </div>
            ))}
          </section>

          <section className="panel">
            <div className="panel-head"><div><h3>Planos disponíveis</h3><p className="muted">Assine e economize</p></div></div>
            {plans.map(p => (
              <div className="client-plan-row" key={p.id} data-testid={`client-plan-${p.id}`}>
                <div>
                  <strong>{p.name}</strong>
                  <span>{p.description} · {p.uses_per_month ? `${p.uses_per_month} usos/mês` : "ilimitado"}</span>
                </div>
                <div className="client-plan-price">
                  <b>{money(p.price)}<small>/mês</small></b>
                  <button data-testid={`client-subscribe-${p.id}`} className="gold-btn small" onClick={() => subscribe(p)}>Assinar</button>
                </div>
              </div>
            ))}
          </section>
        </div>

        {showBook && step === "form" && (
          <div className="modal-backdrop">
            <form data-testid="client-book-form" className="modal wide-modal" onSubmit={goReview}>
              <button type="button" className="modal-close" onClick={() => setShowBook(false)}><X /></button>
              <p className="eyebrow">NOVO AGENDAMENTO · Passo 1 de 2</p>
              <h2>Marcar horário</h2>
              <div className="modal-grid">
                <label>Barbeiro
                  <select data-testid="client-barber-select" value={form.barber_id} onChange={e => setForm({ ...form, barber_id: e.target.value, time: "" })} required>
                    <option value="">Selecione</option>
                    {barbers.map(b => <option key={b.id} value={b.id}>{b.name} · {b.start_time || "09:00"}–{b.end_time || "19:30"}</option>)}
                  </select>
                </label>
                <label>Serviço
                  <select data-testid="client-service-select" value={form.service_id} onChange={e => setForm({ ...form, service_id: e.target.value, time: "" })} required>
                    <option value="">Selecione</option>
                    {services.map(s => <option key={s.id} value={s.id}>{s.name} · {money(s.price)} · {s.duration}min</option>)}
                  </select>
                </label>
                <label className="full-col">Data
                  <input data-testid="client-date-input" type="date" value={form.date} min={new Date().toISOString().slice(0, 10)} onChange={e => setForm({ ...form, date: e.target.value, time: "" })} required />
                </label>
                {mySubs.length > 0 && (
                  <label className="full-col">Usar meu plano (opcional)
                    <select data-testid="client-plan-select" value={form.use_plan_subscription_id} onChange={e => setForm({ ...form, use_plan_subscription_id: e.target.value })}>
                      <option value="">Pagar avulso</option>
                      {mySubs.map(s => <option key={s.id} value={s.id}>Assinatura ativa</option>)}
                    </select>
                  </label>
                )}
              </div>
              <label>Horário disponível</label>
              {slotsInfo.closed_day && <div className="empty small">Barbeiro não atende nesta data.</div>}
              <div className="slot-grid">
                {!slotsInfo.closed_day && slots.length === 0 && <span className="muted">Selecione barbeiro, serviço e data para ver os horários.</span>}
                {slots.map(s => (
                  <button key={s} type="button" data-testid={`client-slot-${s}`} className={`slot ${form.time === s ? "active" : ""}`} onClick={() => setForm({ ...form, time: s })}>{s}</button>
                ))}
              </div>
              {error && <div data-testid="client-book-error" className="error-msg">{error}</div>}
              <button data-testid="client-review-button" className="gold-btn full" disabled={!form.time}>Revisar antes de confirmar</button>
            </form>
          </div>
        )}
        {showBook && step === "review" && (
          <div className="modal-backdrop">
            <div className="modal wide-modal">
              <button type="button" className="modal-close" onClick={() => setShowBook(false)}><X /></button>
              <p className="eyebrow">CONFIRMAÇÃO · Passo 2 de 2</p>
              <h2>Confira antes de confirmar</h2>
              <ul className="review-list">
                <li><span>Nome</span><strong>{user.name}</strong></li>
                <li><span>Telefone</span><strong>{user.phone || myClient?.phone || "—"}</strong></li>
                <li><span>Barbeiro</span><strong>{barberObj?.name}</strong></li>
                <li><span>Serviço</span><strong>{serviceObj?.name} · {serviceObj?.duration}min</strong></li>
                <li><span>Data e horário</span><strong>{form.date} · {form.time}</strong></li>
                <li><span>Valor</span><strong>{form.use_plan_subscription_id ? "Coberto pelo seu plano" : money(serviceObj?.price || 0)}</strong></li>
              </ul>
              {error && <div className="error-msg">{error}</div>}
              <div className="review-actions">
                <button data-testid="client-back-button" className="ghost-btn" onClick={() => setStep("form")}>Voltar</button>
                <button data-testid="client-book-confirm" className="gold-btn" disabled={busy} onClick={confirmBooking}>{busy ? "Salvando..." : "Confirmar agendamento"}</button>
              </div>
              <p className="muted small">Ao confirmar, você receberá uma mensagem de WhatsApp com os detalhes.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
